// A small router on the History API: paths for every screen, the query string (demo flags) kept as it is.
import { useSyncExternalStore } from 'react';

export type Route =
  | { name: 'today' }
  | { name: 'entries' }
  | { name: 'reports' }
  | { name: 'sessions' }
  | { name: 'session'; id: string }
  | { name: 'projects' }
  | { name: 'project'; id: string }
  | { name: 'integrations' }
  | { name: 'settings'; tab: string }
  | { name: 'pro' }
  | { name: 'privacy' }
  | { name: 'support' }
  | { name: 'login' };

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  switch (parts[0]) {
    case 'entries': return { name: 'entries' };
    case 'reports': return { name: 'reports' };
    case 'sessions': return parts[1] ? { name: 'session', id: parts[1] } : { name: 'sessions' };
    case 'projects': return parts[1] ? { name: 'project', id: parts[1] } : { name: 'projects' };
    case 'integrations': return { name: 'integrations' };
    case 'settings': return { name: 'settings', tab: parts[1] ?? 'general' };
    case 'pro': return { name: 'pro' };
    case 'privacy': case 'datenschutz': return { name: 'privacy' };
    case 'support': case 'hilfe': return { name: 'support' };
    case 'login': return { name: 'login' };
    default: return { name: 'today' };
  }
}

export function routePath(route: Route): string {
  switch (route.name) {
    case 'today': return '/';
    case 'session': return `/sessions/${encodeURIComponent(route.id)}`;
    case 'project': return `/projects/${encodeURIComponent(route.id)}`;
    case 'settings': return route.tab === 'general' ? '/settings' : `/settings/${route.tab}`;
    default: return `/${route.name}`;
  }
}

const listeners = new Set<() => void>();

export function navigate(route: Route | string, options: { replace?: boolean } = {}): void {
  const path = typeof route === 'string' ? route : routePath(route);
  const url = path + window.location.search;
  if (options.replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
  for (const l of listeners) l();
  window.scrollTo({ top: 0 });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
}

export function useRoute(): Route {
  const pathname = useSyncExternalStore(subscribe, () => window.location.pathname, () => '/');
  return parseRoute(pathname);
}
